import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Label, Spinner, TextInput, Textarea } from "flowbite-react";
import { HiExternalLink } from "react-icons/hi";
import { auctionPropertiesThunks } from "../../app/store";
import { auctionPropertiesApi } from "../../api/auctionPropertiesApi";
import PropertyMediaQueueGrid from "./shared/PropertyMediaQueueGrid";
import PropertyMediaSavedGrid from "./shared/PropertyMediaSavedGrid";
import PropertyMediaUploadPanel from "./shared/PropertyMediaUploadPanel";
import { usePropertyMediaManager } from "./shared/usePropertyMediaManager";

const MEDIA_TYPE = "FEMA_IMAGE";

function buildFemaUrl({ latitude, longitude, address } = {}) {
  const lng = (longitude ?? "").toString().trim();
  const lat = (latitude ?? "").toString().trim();

  if (lng && lat) {
    // FEMA expects coordinates in longitude,latitude order when searching.
    return `https://msc.fema.gov/portal/search?AddressQuery=${encodeURIComponent(`${lng},${lat}`)}`;
  }

  const trimmedAddress = (address || "").trim();
  if (trimmedAddress) {
    return `https://msc.fema.gov/portal/search?AddressQuery=${encodeURIComponent(trimmedAddress)}`;
  }

  return "https://msc.fema.gov/portal/home";
}

export default function Step4Fema({ propertyId, property, onSave }) {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth?.user?.id);

  const [form, setForm] = useState({
    fema_zone_code: property?.fema_zone_code || "",
    fema_risk_notes: property?.fema_risk_notes || "",
  });
  const [labelBase, setLabelBase] = useState("fema");
  const [saving, setSaving] = useState(false);
  const [syncingFema, setSyncingFema] = useState(false);

  const hasCoordinates =
    property?.latitude !== null &&
    property?.latitude !== undefined &&
    property?.longitude !== null &&
    property?.longitude !== undefined;

  const {
    deletingId,
    onDeleteMedia,
    onFileInputChange,
    pasteBoxRef,
    queue,
    removeFromQueue,
    savedMedia,
    setUploadError,
    updateQueueLabel,
    uploadError,
    uploading,
    uploadQueue,
  } = usePropertyMediaManager({
    property,
    propertyId,
    onSave,
    mediaType: MEDIA_TYPE,
    labelBase,
    defaultClipboardBase: "fema",
    userId,
  });

  useEffect(() => {
    setForm({
      fema_zone_code: property?.fema_zone_code || "",
      fema_risk_notes: property?.fema_risk_notes || "",
    });
  }, [propertyId, property?.fema_zone_code, property?.fema_risk_notes]);

  async function handleSyncFema() {
    if (!hasCoordinates) return;

    try {
      setUploadError(null);
      setSyncingFema(true);

      const data = await auctionPropertiesApi.syncFema({
        latitude: property.latitude,
        longitude: property.longitude,
      });

      setForm((current) => ({
        ...current,
        fema_zone_code: data?.fema_zone_code || "",
        fema_risk_notes: data?.fema_risk_notes || "",
      }));
    } catch (error) {
      setUploadError(error?.message || "Erro ao sincronizar dados FEMA");
    } finally {
      setSyncingFema(false);
    }
  }

  async function saveFields() {
    if (!propertyId) return;
    setSaving(true);
    try {
      await dispatch(
        auctionPropertiesThunks.updateOne({
          id: propertyId,
          payload: {
            fema_zone_code: form.fema_zone_code?.trim() || null,
            fema_risk_notes: form.fema_risk_notes?.trim() || null,
          },
        }),
      ).unwrap();

      onSave?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          className="gap-2"
          color="dark"
          onClick={() =>
            window.open(
              buildFemaUrl({
                latitude: property?.latitude,
                longitude: property?.longitude,
                address: property?.property_address,
              }),
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          FEMA <HiExternalLink />
        </Button>

        <Button
          color="dark"
          onClick={handleSyncFema}
          disabled={syncingFema || !hasCoordinates}
        >
          {syncingFema ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Syncing FEMA
            </span>
          ) : (
            "Sync FEMA Data"
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>FEMA Zone Code</Label>
          <TextInput
            value={form.fema_zone_code}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                fema_zone_code: event.target.value,
              }))
            }
            placeholder="Ex: AE, X, VE..."
          />
        </div>

        <div className="md:col-span-2">
          <Label>FEMA Risk Notes</Label>
          <Textarea
            rows={4}
            value={form.fema_risk_notes}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                fema_risk_notes: event.target.value,
              }))
            }
            placeholder="Ex: Flood zone AE. Base flood elevation... Observações do site FEMA..."
          />
        </div>

        <div>
          <Label>Nome base das imagens</Label>
          <TextInput
            value={labelBase}
            onChange={(event) => setLabelBase(event.target.value)}
            placeholder="Ex: fema-zone, floodmap, panel"
          />
        </div>
      </div>

      <PropertyMediaUploadPanel
        helperText="Clique nesta caixa e cole. As imagens vão aparecer para pré-visualização antes de enviar."
        onFileInputChange={onFileInputChange}
        onSave={saveFields}
        onUpload={uploadQueue}
        pasteBoxRef={pasteBoxRef}
        queueLength={queue.length}
        saveButtonLabel="Salvar Step 4"
        saving={saving}
        title="Cole prints do FEMA aqui (Ctrl+V)"
        uploadError={uploadError}
        uploading={uploading}
      />

      <PropertyMediaQueueGrid
        items={queue}
        labelPlaceholder="Ex: fema-panel-123"
        onChangeLabel={updateQueueLabel}
        onRemove={removeFromQueue}
      />

      <PropertyMediaSavedGrid
        deletingId={deletingId}
        items={savedMedia}
        onDelete={onDeleteMedia}
        sectionTitle="Imagens já anexadas (FEMA)"
        typeLabel="fema"
      />
    </div>
  );
}
